import { transactionLabel } from '../utils/transactionLabels';
import React, { useEffect, useState } from 'react';
import BulkTransactionRenameDialog from './BulkTransactionRenameDialog';
import TransactionBulkDialog from './TransactionBulkDialog';
import SplitActivity from './SplitActivity';
import SplitEventDialog from './SplitEventDialog';
import { giftColor, GiftAmount, GiftSentChip } from './GiftPresentation';
import { activityItems } from '../utils/splitActivity';
import { exportSelectedTransactions } from '../services/api';
import { selectionSummary, money } from '../utils/transactionSelection';
import CollectionSearch from './search/CollectionSearch';
import ReceiptViewer from './intake/ReceiptViewer';
import { filterRecords } from '../utils/search';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
  Chip,
  Box,
  Alert,
  Checkbox, Tabs, Tab, Menu, MenuItem
} from '@mui/material';

function TransactionTable({ transactions, accounts, categories, onEdit, onDelete, onTransactionsUpdated }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedActions, setExpandedActions] = useState(new Set());
  const [search, setSearch] = useState('');
  const [receiptId, setReceiptId] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [renameOpen, setRenameOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('delete');
  const [view, setView] = useState('activity');
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [eventContext, setEventContext] = useState(null);
  const [bulkError, setBulkError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const available = new Set((transactions || []).map(t => t.id));
    setSelected(old => new Set([...old].filter(id => available.has(id))));
  }, [transactions]);
  const toggleSelected = id => setSelected(old => {
    const next = new Set(old);
    if (next.has(id)) next.delete(id);
    else if (next.size < 100) next.add(id);
    return next;
  });
  const selectionControl = transaction => <Checkbox checked={selected.has(transaction.id)}
    sx={{ minWidth: 44, minHeight: 44 }}
    disabled={!selected.has(transaction.id) && selected.size >= 100}
    onChange={() => toggleSelected(transaction.id)}
    inputProps={{ 'aria-label': `Select transaction ${transaction.id}: ${transaction.description || '(empty)'}` }} />;

  const MobileTransactionCard = ({ transaction }) => {
    const [expanded, setExpanded] = useState(false);
    const gift = transaction.user_label === 'gift';
    
    return (
      <Card sx={{ mb: 2, bgcolor: selected.has(transaction.id) ? 'action.selected' : undefined }}>
        <CardContent sx={{ pb: expanded ? 2 : 1 }}>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {selecting && selectionControl(transaction)}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere' }}>
                {transaction.description}
              </Typography>
              {gift ? <GiftAmount amount={transaction.amount} signed variant="h6" sx={{ ml: 1 }} /> : <Typography
                variant="h6" 
                color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                fontWeight="bold"
                sx={{ flexShrink: 0, ml: 1 }}
              >
                ${Math.abs(transaction.amount).toFixed(2)}
              </Typography>}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {transaction.transaction_date}
              </Typography>
              {gift ? <GiftSentChip /> : <Chip
                label={transactionLabel(transaction)}
                size="small" 
                color={transaction.transaction_nature === 'transfer' ? 'info' : transaction.amount > 0 ? 'success' : 'error'}
                variant="outlined"
              />}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={getAccountName(transaction.account_id)} 
                size="small"
                variant="outlined"
              />
              <Chip 
                label={getCategoryName(transaction.category_id, transaction.account_id)} 
                size="small"
              />
            </Box>
            
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ 
                alignSelf: 'flex-start',
                minHeight: 44,
                px: 2
              }}
            >
              {expanded ? 'Hide Actions' : 'Show Actions'}
            </Button>
            {transaction.receipt_intake_id && <Button size="small" onClick={() => setReceiptId(transaction.receipt_intake_id)}>View original receipt</Button>}
            {(transaction.split_activity || transaction.income_split_submission_id) && <Button size="small" onClick={() => setEventContext({ anchorId: transaction.id })}>View split event</Button>}
            
            {expanded && (
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  fullWidth
                  onClick={() => onEdit(transaction)}
                  sx={{ py: 1.5 }}
                >
                  Edit Transaction
                </Button>
                <Button 
                  variant="outlined" 
                  color="error" 
                  fullWidth
                  onClick={() => onDelete(transaction.id)}
                  sx={{ py: 1.5 }}
                >
                  Delete Transaction
                </Button>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (!transactions || transactions.length === 0) {
    return <Typography variant="subtitle1">No transactions to display.</Typography>;
  }

  const getAccountName = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    if (!account) return 'N/A';
    return account.account_status === 'archived' ? `${account.name} (Archived)` : account.name;
  };

  const getCategoryName = (categoryId, accountId) => {
    const category = categories.find(cat => cat.id === categoryId);
    const account = accounts.find(acc => acc.id === accountId);
    
    if (!category) return 'N/A';
    
    // If the account is archived, show category as archived too
    if (account && account.account_status === 'archived') {
      return `${category.name} (Archived)`;
    }
    
    // Otherwise just check if category itself is deleted
    return category.is_deleted ? `${category.name} (Archived)` : category.name;
  };

  const visibleTransactions = filterRecords(transactions, search, transaction => [
    transaction.description, transactionLabel(transaction), transaction.amount, transaction.transaction_date,
    getAccountName(transaction.account_id), getCategoryName(transaction.category_id, transaction.account_id),
  ]);
  const selectedRows = transactions.filter(t => selected.has(t.id));
  const summary = selectionSummary(selectedRows, accounts, categories);
  const activities = activityItems(visibleTransactions);
  const canChangeRows = selectedRows.length > 0 && selectedRows.every(row => !row.workflow_linked &&
    (row.transaction_nature !== 'transfer' || (row.linked_transaction_id && selected.has(row.linked_transaction_id))));
  const canChangeCategory = canChangeRows && new Set(selectedRows.map(row => row.account_id)).size === 1 &&
    selectedRows.every(row => row.transaction_nature !== 'transfer');
  const chooseAction = action => { setActionsAnchor(null); setBulkAction(action); setBulkOpen(true); };
  const downloadSelection = async () => {
    if (exporting) return;
    setExporting(true); setBulkError('');
    try {
      const response = await exportSelectedTransactions([...selected]);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a'); link.href = url; link.download = 'selected-transactions.csv';
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { setBulkError('Could not export the selected entries. No records were changed.'); }
    finally { setExporting(false); }
  };

  return (
    <>
      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}
      {bulkError && <Alert severity="error">{bulkError}</Alert>}
      <Tabs value={view} onChange={(_, next) => { setView(next); setSelected(new Set()); setSelecting(false); setActionsAnchor(null); }}
        variant="fullWidth" aria-label="Transaction view" sx={{ mb: 2 }}>
        <Tab value="activity" label="Activity" /><Tab value="entries" label="Ledger entries" />
      </Tabs>
      <CollectionSearch label="Search transactions" value={search} onChange={value => { setSearch(value); setSelected(new Set()); }}
        total={view === 'activity' ? activityItems(transactions).length : transactions.length}
        shown={view === 'activity' ? activities.length : visibleTransactions.length} />
      {view === 'entries' && onTransactionsUpdated && <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
        <Button onClick={() => { setSelecting(!selecting); setSelected(new Set()); }}>
          {selecting ? 'Cancel selection' : 'Select transactions'}
        </Button>
        {selecting && <>
          <Typography variant="body2">{selected.size} selected (up to 100)</Typography>
          <Button disabled={!visibleTransactions.length || visibleTransactions.length > 100}
            onClick={() => setSelected(new Set(visibleTransactions.map(t => t.id)))}>Select all shown</Button>
          <Button variant="contained" disabled={!selected.size || exporting} onClick={e => setActionsAnchor(e.currentTarget)}>Actions</Button>
          <Menu anchorEl={actionsAnchor} open={!!actionsAnchor} onClose={() => setActionsAnchor(null)}>
            <MenuItem onClick={() => { setActionsAnchor(null); setRenameOpen(true); }}>Edit selected descriptions</MenuItem>
            <MenuItem onClick={() => { setActionsAnchor(null); downloadSelection(); }}>Export selected</MenuItem>
            {canChangeRows && <MenuItem onClick={() => chooseAction('date')}>Change selected dates</MenuItem>}
            {canChangeCategory && <MenuItem onClick={() => chooseAction('category')}>Change selected categories</MenuItem>}
            {canChangeRows && <MenuItem onClick={() => chooseAction('delete')}>Delete selected</MenuItem>}
            <MenuItem onClick={() => { setActionsAnchor(null); setSelected(new Set()); }}>Clear selection</MenuItem>
          </Menu>
        </>}
      </Stack>}
      {selecting && selected.size > 0 && <Box aria-label="Selection summary" sx={{ mb: 2 }}>
        <Typography fontWeight="bold">Selected net: {money(summary.net)}</Typography>
        {summary.groups.map(g => <Typography variant="body2" key={g.key}>{g.account} / {g.category}: {money(g.amount)} ({g.count})</Typography>)}
      </Box>}
      {visibleTransactions.length === 0 && <Typography sx={{ mb: 2 }}>No matching transactions.</Typography>}
      {view === 'activity' ? <SplitActivity items={activities} accounts={accounts} categories={categories} onEdit={onEdit} onDelete={onDelete}
        editable={!!onTransactionsUpdated} onReceipt={setReceiptId}
        onOpenEvent={(anchorId, editing) => setEventContext({ anchorId, editing: editing && !!onTransactionsUpdated })} /> : isMobile ? (
        <Box sx={{ px: 2, py: 1 }}>
          {visibleTransactions.map((transaction) => (
            <MobileTransactionCard 
              key={transaction.id} 
              transaction={transaction} 
            />
          ))}
          {visibleTransactions.length === 0 && (
            <Card>
              <CardContent>
                <Typography color="text.secondary" align="center">
                  No transactions found
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="transaction table">
            <TableHead>
              <TableRow>
                {selecting && <TableCell padding="checkbox">Select</TableCell>}
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTransactions.map((transaction) => (
                <TableRow
                  key={transaction.id}
                  selected={selected.has(transaction.id)}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  {selecting && <TableCell padding="checkbox">{selectionControl(transaction)}</TableCell>}
                  <TableCell component="th" scope="row">
                    {transaction.transaction_date}
                  </TableCell>
                  <TableCell>{transaction.description}{transaction.user_label === 'gift' && <GiftSentChip sx={{ ml: 1 }} />}</TableCell>
                  <TableCell align="right" sx={transaction.user_label === 'gift' ? { color: giftColor } : undefined}>{transaction.amount}</TableCell>
                  <TableCell>{getCategoryName(transaction.category_id, transaction.account_id)}</TableCell>
                  <TableCell>{getAccountName(transaction.account_id)}</TableCell>
                  <TableCell>
                    {transaction.receipt_intake_id && <Button size="small" onClick={() => setReceiptId(transaction.receipt_intake_id)}>View original receipt</Button>}
                    {(transaction.split_activity || transaction.income_split_submission_id) && <Button size="small" onClick={() => setEventContext({ anchorId: transaction.id })}>View split event</Button>}
                    {expandedActions.has(transaction.id) ? (
                      <Box>
                        <Button variant="outlined" size="small" onClick={() => onEdit(transaction)}>
                          Edit
                        </Button>
                        <Button variant="outlined" color="error" size="small" sx={{ ml: 1 }} onClick={() => onDelete(transaction.id)}>
                          Delete
                        </Button>
                        <Button size="small" sx={{ ml: 1 }} onClick={() => {
                          const newExpanded = new Set(expandedActions);
                          newExpanded.delete(transaction.id);
                          setExpandedActions(newExpanded);
                        }}>
                          Hide
                        </Button>
                      </Box>
                    ) : (
                      <Button size="small" onClick={() => {
                        const newExpanded = new Set(expandedActions);
                        newExpanded.add(transaction.id);
                        setExpandedActions(newExpanded);
                      }}>
                        Show Actions
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ReceiptViewer open={!!receiptId} entryId={receiptId} onClose={() => setReceiptId(null)} />
      <SplitEventDialog anchorId={eventContext?.anchorId} startEditing={!!eventContext?.editing} editable={!!onTransactionsUpdated}
        onReceipt={setReceiptId}
        onClose={uncertain => { setEventContext(null); if (uncertain) onTransactionsUpdated?.(); }}
        onCorrected={result => {
          setEventContext(null); setSelected(new Set()); setSelecting(false);
          onTransactionsUpdated(`Corrected ${result.transaction_type === 'income' ? 'deposit' : 'purchase split'}: replaced ${result.count} entries. Template unchanged.`);
        }} />
      <TransactionBulkDialog open={bulkOpen} transactions={selectedRows} action={bulkAction} categories={categories} onClose={() => setBulkOpen(false)} onApplied={result => {
        setBulkOpen(false); setSelected(new Set()); setSelecting(false);
        setNotice(`${bulkAction === 'delete' ? 'Deleted' : 'Updated'} ${result.count} selected entries. Split templates are unchanged.`);
        onTransactionsUpdated(`${result.action === 'delete' || bulkAction === 'delete' ? 'Deleted' : 'Updated'} ${result.count} selected entries. Split templates are unchanged.`);
      }} />
      <BulkTransactionRenameDialog open={renameOpen} transactions={transactions.filter(t => selected.has(t.id))}
        onClose={() => setRenameOpen(false)} onRenamed={changes => {
          setRenameOpen(false); setSelected(new Set()); setSelecting(false);
          setNotice(`Updated ${changes.length} descriptions. Amounts and allocations are unchanged.`);
          onTransactionsUpdated();
        }} />
    </>
  );
}

export default TransactionTable;
